import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import cn from 'classnames';
import classes from './AnalyzeSurveyProgress.module.css';

type AnalyzeSurveyProgressProps = {
  questions: string[];
  /** The question being shown, or 'summary' */
  step: number | 'summary';
};

/** One step per question plus the summary. Every step links to its page. */
export const AnalyzeSurveyProgress = ({ questions, step }: AnalyzeSurveyProgressProps) => {
  const { t } = useTranslation();
  const { teamId, surveyId } = useParams();
  const basePath = `/teams/${teamId}/analyze-survey/${surveyId}`;
  const total = questions.length;
  const isSummary = step === 'summary';
  const currentIndex = isSummary ? total : step - 1;

  const steps = [
    ...questions.map((question, index) => ({
      to: `${basePath}/${index + 1}`,
      label: t('analysis.steps.questionLabel', { number: index + 1, question }),
    })),
    { to: `${basePath}/summary`, label: t('analysis.steps.summaryLabel') },
  ];

  return (
    <nav aria-label={t('analysis.steps.ariaLabel')} className={classes.progress}>
      <div className={classes.header}>
        <span className={classes.stepLabel}>
          {isSummary
            ? t('analysis.steps.summaryStep')
            : t('analysis.steps.questionOf', { current: step, total })}
        </span>
        {isSummary && <span className={classes.area}>{t('analysis.steps.wholeTeam')}</span>}
      </div>
      <ol className={classes.steps} style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
        {steps.map(({ to, label }, index) => (
          <li key={to}>
            <Link
              to={to}
              aria-label={label}
              aria-current={index === currentIndex ? 'step' : undefined}
              className={classes.step}
            >
              <span
                className={cn(classes.bar, {
                  [classes.current]: index === currentIndex,
                  [classes.summaryCurrent]: index === currentIndex && isSummary,
                  [classes.visited]: index < currentIndex,
                })}
              />
            </Link>
          </li>
        ))}
      </ol>
    </nav>
  );
};
